import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, Portal, Text, Textarea } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import { voidPayment } from '@/lib/api';

/**
 * Confirm + reason dialog for voiding a `regular` payment (admin-only).
 * The payment row (and its receipt) is retained for audit — voiding just
 * reverses the applied amounts and flags the payment as `voided`.
 *
 * Always mounted by the parent; visibility is driven by `paymentId` being
 * non-null (idiomatic Chakra v3 controlled pattern — same as
 * ReplaceReceiptButton). Previously the parent conditionally MOUNTED this
 * component only while voiding, with the Dialog.Root hardcoded `open` — so
 * "closing" it unmounted an Ark dialog machine mid-"open" state instead of
 * letting it run its normal open→closed transition. Under React StrictMode
 * that skipped teardown (focus-trap deactivate, preventScroll cleanup) and
 * either threw uncaught (blanking the SPA, no ErrorBoundary exists) or left
 * `document.body` stuck with `pointer-events: none`. Keeping it mounted lets
 * Ark tear down cleanly before any unmount happens.
 */
export function VoidPaymentDialog({
  paymentId,
  receiptNumber,
  onClose,
  onDone,
}: {
  paymentId: string | null;
  receiptNumber?: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const open = paymentId != null;

  // Reset the reason text each time a new void flow starts (the dialog no
  // longer remounts between opens, so state wouldn't otherwise clear).
  useEffect(() => {
    if (open) setReason('');
  }, [open, paymentId]);

  async function submit() {
    if (!paymentId) return;
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      toaster.create({ title: 'Ingresa un motivo (mín. 3 caracteres)', type: 'warning' });
      return;
    }
    setBusy(true);
    try {
      await voidPayment(paymentId, trimmed);
      toaster.create({
        title: 'Pago anulado',
        description: receiptNumber ? `Recibo ${receiptNumber}` : undefined,
        type: 'success',
      });
      onDone();
    } catch (err) {
      toaster.create({
        title: 'No se pudo anular el pago',
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog.Root
      role="alertdialog"
      open={open}
      size="sm"
      // Ark's alertdialog default for `initialFocusEl` is the close button
      // (here nonexistent, since we render no Dialog.CloseTrigger) — pin
      // explicit, always-valid resolvers so focus-trap setup/teardown never
      // has to chase a missing node: the textarea while mounted, `document.
      // body` (never detached) as the guaranteed fallback.
      initialFocusEl={() => textareaRef.current ?? document.body}
      finalFocusEl={() => document.body}
      onOpenChange={(d) => {
        if (!d.open) onClose();
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Anular pago{receiptNumber ? ` ${receiptNumber}` : ''}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Text fontSize="sm" color="fg.muted" mb="3">
                El pago se conserva para auditoría y se revierte lo aplicado a las cuotas. Deberás
                registrar el pago correcto nuevamente.
              </Text>
              <Textarea
                ref={textareaRef}
                placeholder="Motivo de la anulación (mín. 3 caracteres)"
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
              />
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" onClick={onClose} disabled={busy}>
                Cancelar
              </Button>
              <Button colorPalette="red" loading={busy} onClick={submit}>
                Anular pago
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
